<template>
  <div class="landing-page">
    <!-- Hero Section -->
    <section class="hero-section text-center py-8 py-md-16">
      <div class="hero-badge mb-4">
        <v-icon size="16" color="primary" class="mr-1">mdi-sparkles</v-icon>
        <span>Your Personal Media Universe</span>
      </div>

      <h1 class="hero-title mb-4">
        Catalog, Track & Relive Your <br class="d-none d-md-inline" />
        <span class="text-gradient-primary">Favorite Entertainment</span>
      </h1>

      <p class="hero-subtitle mb-8 text-medium-emphasis">
        A unified personal vault for your Movies, Books, Audiobooks, and Comics.
        Organized beautifully with live metadata, custom wishlists, and seamless category shelves.
      </p>

      <div class="d-flex justify-center flex-wrap ga-3 mb-12">
        <template v-if="!isAuthenticated">
          <v-btn
            to="/register"
            size="x-large"
            color="primary"
            class="btn-modern btn-glow-primary px-6"
            prepend-icon="mdi-rocket-launch-outline"
          >
            Get Started Free
          </v-btn>
          <v-btn
            to="/login"
            size="x-large"
            variant="outlined"
            class="btn-modern px-6"
            prepend-icon="mdi-login"
          >
            Sign In
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            to="/homeview"
            size="x-large"
            color="primary"
            class="btn-modern btn-glow-primary px-8"
            prepend-icon="mdi-view-grid"
          >
            Explore My Collections
          </v-btn>
        </template>
      </div>

      <!-- Interactive 4-Collection Showcase Grid -->
      <v-row justify="center" class="showcase-grid mb-12" dense>
        <v-col
          v-for="item in showcases"
          :key="item.id"
          cols="12"
          sm="6"
          lg="3"
        >
          <v-card
            :to="isAuthenticated ? item.to : '/login'"
            class="showcase-card h-100 text-left pa-5"
            :class="`showcase-card--${item.id}`"
          >
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="showcase-icon-box" :style="{ background: item.iconBg }">
                <v-icon :color="item.color" size="28">{{ item.icon }}</v-icon>
              </div>
              <v-chip size="x-small" :color="item.color" variant="tonal" class="font-weight-bold">
                {{ item.tag }}
              </v-chip>
            </div>

            <h3 class="text-h6 font-weight-bold mb-1">{{ item.title }}</h3>
            <p class="text-body-2 text-medium-emphasis mb-4">{{ item.desc }}</p>

            <div class="d-flex align-center text-caption font-weight-bold" :style="{ color: item.color }">
              <span>{{ isAuthenticated ? 'Browse Shelf' : 'Sign in to access' }}</span>
              <v-icon size="16" class="ml-1">mdi-arrow-right</v-icon>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <!-- Key Features Section -->
    <section class="features-section py-8">
      <div class="text-center mb-8">
        <h2 class="text-h4 font-weight-bold mb-2">Designed for Media Lovers</h2>
        <p class="text-body-1 text-medium-emphasis">Everything you need to organize your collection in one place</p>
      </div>

      <v-row>
        <v-col cols="12" md="4">
          <v-card class="glass-card pa-6 h-100">
            <v-icon color="primary" size="36" class="mb-3">mdi-auto-fix</v-icon>
            <h3 class="text-h6 font-weight-bold mb-2">Smart TMDB Autofill</h3>
            <p class="text-body-2 text-medium-emphasis">
              Type any movie title and let Media Catalog instantly fetch posters, synopsis, release year, cast photos, and YouTube trailers.
            </p>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="glass-card pa-6 h-100">
            <v-icon color="secondary" size="36" class="mb-3">mdi-bookmark-heart-outline</v-icon>
            <h3 class="text-h6 font-weight-bold mb-2">Wishlist & Favorites</h3>
            <p class="text-body-2 text-medium-emphasis">
              Keep track of what you want to watch or read next. Move items from wishlist to your owned collection with a single click.
            </p>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="glass-card pa-6 h-100">
            <v-icon color="accent" size="36" class="mb-3">mdi-view-carousel-outline</v-icon>
            <h3 class="text-h6 font-weight-bold mb-2">Shelf & Grid Views</h3>
            <p class="text-body-2 text-medium-emphasis">
              Browse your library by category swimlanes or switch to a dense responsive poster grid with live filters and sorting.
            </p>
          </v-card>
        </v-col>
      </v-row>
    </section>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'Home',
  setup() {
    const store = useStore()
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])

    const showcases = [
      {
        id: 'movies',
        title: 'Movies',
        desc: 'Curate your cinema library with ratings, 4K/HD quality badges, and YouTube trailers.',
        icon: 'mdi-movie-open',
        color: '#6366f1',
        iconBg: 'rgba(99, 102, 241, 0.15)',
        tag: 'CINEMA',
        to: '/movies/homeview',
      },
      {
        id: 'books',
        title: 'Books & Novels',
        desc: 'Organize your physical and digital bookshelf with authors, categories, and reading status.',
        icon: 'mdi-book-open-page-variant',
        color: '#10b981',
        iconBg: 'rgba(16, 185, 129, 0.15)',
        tag: 'READING',
        to: '/books/homeview',
      },
      {
        id: 'audiobooks',
        title: 'AudioBooks',
        desc: 'Catalog your audiobooks, narrators, durations, and audio quality tags.',
        icon: 'mdi-headphones',
        color: '#f43f5e',
        iconBg: 'rgba(244, 63, 94, 0.15)',
        tag: 'AUDIO',
        to: '/audiobooks/homeview',
      },
      {
        id: 'comics',
        title: 'Comics & Manga',
        desc: 'Track comic series, graphic novels, tome volumes, and illustrators with ease.',
        icon: 'mdi-draw',
        color: '#f59e0b',
        iconBg: 'rgba(245, 158, 11, 0.15)',
        tag: 'SERIES',
        to: '/comics/homeview',
      },
    ]

    return {
      isAuthenticated,
      showcases,
    }
  },
}
</script>

<style scoped>
.landing-page {
  max-width: 1280px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.25);
  color: #6366f1;
}

.hero-title {
  font-size: clamp(2rem, 5vw, 3.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.25rem);
  max-width: 740px;
  margin: 0 auto;
  line-height: 1.6;
}

.showcase-card {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(128, 128, 128, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  text-decoration: none;
  color: inherit;
}

.v-theme--dark .showcase-card {
  background: rgba(18, 24, 38, 0.7) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.showcase-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.25);
}

.showcase-card--movies:hover {
  border-color: rgba(99, 102, 241, 0.5) !important;
  box-shadow: 0 20px 35px -10px rgba(99, 102, 241, 0.3);
}

.showcase-card--books:hover {
  border-color: rgba(16, 185, 129, 0.5) !important;
  box-shadow: 0 20px 35px -10px rgba(16, 185, 129, 0.3);
}

.showcase-card--audiobooks:hover {
  border-color: rgba(244, 63, 94, 0.5) !important;
  box-shadow: 0 20px 35px -10px rgba(244, 63, 94, 0.3);
}

.showcase-card--comics:hover {
  border-color: rgba(245, 158, 11, 0.5) !important;
  box-shadow: 0 20px 35px -10px rgba(245, 158, 11, 0.3);
}

.showcase-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
}
</style>

